import { useCallback } from 'react';

import { SHAPE_ROTATION_DEGREES } from '~/core/constants';
import { DrawingAppliance, DrawingShape } from '~/core/models';
import { isEqApplianceId, makeApplianceName } from '~/core/utils';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import { setPreventRepricing } from '~/redux/slices/drawingShape';
import {
  clearSelectedDrawingTabs,
  updateSelectedDrawingObjectName,
} from '~/redux/slices/shape';

export const useActiveAppliance = () => {
  const dispatch = useAppDispatch();

  const {
    getters: { drawingShapes, activeDrawingApplianceId },
    handlers: { updateActiveDrawingAppliance },
  } = useDrawingShapeContext();

  const activateAddedAppliance = useCallback(
    (drawingAppliance: DrawingAppliance) => {
      const {
        applianceModel: { consumerDesc, name },
      } = drawingAppliance;
      const applianceName = consumerDesc ?? name;

      updateActiveDrawingAppliance(makeApplianceName(drawingAppliance));
      dispatch(updateSelectedDrawingObjectName(applianceName));
    },
    [dispatch, updateActiveDrawingAppliance]
  );

  const rotateActiveDrawingAppliance = useCallback(
    (canvas: fabric.Canvas, clockwise: boolean) => {
      drawingShapes.forEach((shape: DrawingShape) => {
        const applianceIndex = shape.appliances.findIndex(
          isEqApplianceId(activeDrawingApplianceId)
        );

        if (applianceIndex > -1) {
          dispatch(setPreventRepricing(true));
          shape.appliances[applianceIndex].angle -=
            SHAPE_ROTATION_DEGREES * (clockwise ? -1 : 1);

          canvas
            .getActiveObject()
            ?.rotate(shape.appliances[applianceIndex].angle);
          dispatch(clearSelectedDrawingTabs());
        }
      });
    },
    [activeDrawingApplianceId, dispatch, drawingShapes]
  );

  const getAppliancesFromDrawingShapes = useCallback(
    (productId: number): DrawingAppliance[] =>
      drawingShapes.flatMap((shape: DrawingShape) =>
        shape.appliances.filter(
          (appliance: DrawingAppliance) =>
            appliance.applianceModel.productId === productId
        )
      ),
    [drawingShapes]
  );

  return {
    activateAddedAppliance,
    getAppliancesFromDrawingShapes,
    rotateActiveDrawingAppliance,
  };
};
